import React, { useCallback, useMemo } from "react";
import {
  useAccount,
  useBalance,
  usePublicClient,
  useReadContract,
} from "wagmi";
import SvgSpinner from "./svgspinner";
import { parseAbi, formatUnits } from "viem";
import { Network, Alchemy } from "alchemy-sdk";
import { useState, useEffect } from "react";
import { toBigInt } from "ethers";

export const pepecoinCA = process.env.REACT_APP_PEPECOIN_CONTRACT;
export const basedCA = "0x44971abf0251958492fee97da3e5c5ada88b9185";
export const brainCreditCA = process.env.REACT_APP_BRAIN_CREDITS;
export const brain721CA = process.env.REACT_APP_BRAIN_ERC721_CA;

const settings = {
  apiKey: process.env.REACT_APP_ALCHEMY,
  network: Network.ETH_MAINNET,
};
const alchemy = new Alchemy(settings);

export const BRAIN_NFT_ABI = parseAbi([
  "function ownerOf(uint256 tokenId) view returns (address)",
  "function balanceOf(address owner) view returns (uint256)",
  "function getStakedAmount(address owner) view returns (uint256)",
  "function contributions(address) view returns (uint256)",
  "function contributors(uint256) view returns (address)",
  "function brainToERC20(uint256) view returns (address)",
  "function tokenCounter() view returns (uint256)",
  "function brainMetadata(uint256 tokenId) view returns (string name, string ticker, string metadataUrl, string imageUrl)",
  "function batchContributions(uint256 batchId, address contributor) view returns (uint256)",
  "function currentBatchId() view returns (uint256)",
  "function batchTotalContributions(uint256 batchId) view returns (uint256)",
  "function batchMinted(uint256 batchId) view returns (bool)",
  "function tokensClaimed(uint256 batchId, address contributor) view returns (bool)",
  "function batchClaimed(uint256 batchId) view returns (bool)",
  "function collectiveMint(uint256 batchId) nonpayable",
  "function claimTokens(uint256 batchId) nonpayable",
]);

export const useTokenBalance = (token, watch = true) => {
  const { address } = useAccount();
  return useBalance({
    address,
    token,
    watch,
  });
};

const useContractRead = (address, functionName, args) => {
  return useReadContract({
    address,
    abi: BRAIN_NFT_ABI,
    functionName,
    args,
  });
};

export const useNftsForUser = (userAddress) => {
  const [nfts, setNfts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const publicClient = usePublicClient();

  useEffect(() => {
    const fetchNfts = async () => {
      try {
        const userNfts = await alchemy.nft.getNftsForOwner(userAddress);

        // Filter Brain NFTs
        const brainNfts = userNfts.ownedNfts.filter(
          (nft) =>
            nft.contract.address.toLowerCase() ===
            process.env.REACT_APP_BRAIN_ERC721_CA.toLowerCase()
        );

        const nftsWithMetadata = await Promise.all(
          brainNfts.map(async (nft) => {
            const tokenId = toBigInt(nft.tokenId);
            const metadata = await publicClient.readContract({
              address: brain721CA,
              abi: BRAIN_NFT_ABI,
              functionName: "brainMetadata",
              args: [tokenId], // Pass tokenId as a single-element array
            });
            return {
              ...nft,
              metadata: {
                name: metadata[0],
                ticker: metadata[1],
                metadataUrl: metadata[2],
                imageUrl: metadata[3],
              },
            };
          })
        );

        setNfts({ ...userNfts, ownedNfts: nftsWithMetadata });
        setLoading(false);
      } catch (err) {
        console.error("NFT fetch error:", err);
        setError(err.message || "Failed to fetch NFTs");
        setLoading(false);
      }
    };

    if (userAddress) {
      fetchNfts();
    }
  }, [userAddress, publicClient]);

  const ownedBrains = useMemo(() => {
    if (!nfts.ownedNfts) return [];

    return nfts.ownedNfts
      .filter(
        (nft) =>
          nft.contract.address.toLowerCase() ===
          process.env.REACT_APP_BRAIN_ERC721_CA.toLowerCase()
      )
      .map((nft) => ({
        tokenId: nft.tokenId,
        metadata: nft.metadata,
      }));
  }, [nfts]);

  return { nfts, ownedBrains, loading, error };
};

const formatBalance = (balance, isLoading, decimals = 18) => {
  if (isLoading) return <SvgSpinner />;
  if (!balance) return <span>0</span>;

  const formattedBalance =
    typeof balance === "bigint"
      ? formatUnits(balance, decimals)
      : balance.formatted;

  return Number(formattedBalance).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 4,
  });
};

export const usePooledCredits = () => {
  const { address } = useAccount();
  const { data: currentBatchId } = useReadContract({
    address: brain721CA,
    abi: BRAIN_NFT_ABI,
    functionName: "currentBatchId",
  });

  const { data: pooledCredits, isLoading: pooledCreditsAreLoading } =
    useReadContract({
      address: brain721CA,
      abi: BRAIN_NFT_ABI,
      functionName: "batchContributions",
      args: [currentBatchId, address],
    });

  return { pooledCredits, pooledCreditsAreLoading };
};

export const useBatchManagement = () => {
  const { address } = useAccount();
  const { data: currentBatchId } = useReadContract({
    address: brain721CA,
    abi: BRAIN_NFT_ABI,
    functionName: "currentBatchId",
  });

  const [batchStatuses, setBatchStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const publicClient = usePublicClient();

  const fetchBatchData = useCallback(async () => {
    if (!address || currentBatchId === undefined) return;

    try {
      const batchData = [];

      // Check all batches from 0 to currentBatchId
      for (let i = 0; i <= Number(currentBatchId); i++) {
        const [contribution, totalContribution, isMinted, isClaimed] =
          await Promise.all([
            publicClient.readContract({
              address: brain721CA,
              abi: BRAIN_NFT_ABI,
              functionName: "batchContributions",
              args: [i, address],
            }),
            publicClient.readContract({
              address: brain721CA,
              abi: BRAIN_NFT_ABI,
              functionName: "batchTotalContributions",
              args: [i],
            }),
            publicClient.readContract({
              address: brain721CA,
              abi: BRAIN_NFT_ABI,
              functionName: "batchMinted",
              args: [i],
            }),
            publicClient.readContract({
              address: brain721CA,
              abi: BRAIN_NFT_ABI,
              functionName: "tokensClaimed",
              args: [i, address],
            }),
          ]);

        if (contribution > 0n) {
          batchData.push({
            batchId: i,
            contribution,
            totalContribution,
            isMinted,
            isClaimed,
            isComplete: totalContribution >= 1000n * 10n ** 18n,
            share:
              (contribution * 1000000n * 10n ** 18n) / (1000n * 10n ** 18n),
          });
        }
      }

      setBatchStatuses(batchData);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching batch data:", err);
      setError(err);
      setLoading(false);
    }
  }, [address, currentBatchId, publicClient]);

  useEffect(() => {
    fetchBatchData();
  }, [fetchBatchData]);

  // Get batches ready for minting
  const readyToMintBatches = useMemo(
    () => batchStatuses.filter((batch) => batch.isComplete && !batch.isMinted),
    [batchStatuses]
  );

  // Get batches ready for claiming
  const readyToClaimBatches = useMemo(
    () => batchStatuses.filter((batch) => batch.isMinted && !batch.isClaimed),
    [batchStatuses]
  );

  // Get total unclaimed tokens across all batches
  const totalUnclaimedTokens = useMemo(
    () => readyToClaimBatches.reduce((acc, batch) => acc + batch.share, 0n),
    [readyToClaimBatches]
  );

  return {
    batchStatuses,
    readyToMintBatches,
    readyToClaimBatches,
    totalUnclaimedTokens,
    loading,
    error,
    refetchBatchData: fetchBatchData,
  };
};

export const useUserBatchContributions = () => {
  const { address } = useAccount();
  const { data: currentBatchId } = useReadContract({
    address: brain721CA,
    abi: BRAIN_NFT_ABI,
    functionName: "currentBatchId",
  });

  const [batchContributions, setBatchContributions] = useState({});
  const [totalPooledCredits, setTotalPooledCredits] = useState(0n);
  const [loading, setLoading] = useState(true);
  const publicClient = usePublicClient();

  useEffect(() => {
    const fetchBatchContributions = async () => {
      if (!currentBatchId || !address) return;

      const contributions = {};
      let total = 0n;

      // Check all batches from 0 to currentBatchId
      for (let i = 0; i <= Number(currentBatchId); i++) {
        const contribution = await publicClient.readContract({
          address: brain721CA,
          abi: BRAIN_NFT_ABI,
          functionName: "batchContributions",
          args: [i, address],
        });

        if (contribution > 0n) {
          contributions[i] = contribution;
          total += contribution;
        }
      }

      setBatchContributions(contributions);
      setTotalPooledCredits(total);
      setLoading(false);
    };

    fetchBatchContributions();
  }, [currentBatchId, address, publicClient]);

  return { batchContributions, totalPooledCredits, loading };
};

export function PepecoinBalance({ noImg }) {
  const { data: pepecoinBalance, isLoading } = useTokenBalance(pepecoinCA);

  return (
    <span>
      {!noImg && (
        <img
          src="img/icon-pepe-coin.png"
          style={{ height: "1.75rem", marginRight: "0.5rem" }}
          alt="Pepecoin Logo"
        />
      )}
      {formatBalance(pepecoinBalance, isLoading)}
    </span>
  );
}

export function BrainBalance() {
  const { address } = useAccount();
  const { data: brainBalance, isLoading } = useContractRead(
    brain721CA,
    "balanceOf",
    [address]
  );

  return (
    <span className={brainBalance < 1 ? "muted" : ""}>
      {brainBalance < 1
        ? "You have no Brain NFTs"
        : formatBalance(brainBalance, isLoading, 0)}
    </span>
  );
}
export function StakedBalance() {
  const { address } = useAccount();
  const { data: stakedAmount, isLoading } = useContractRead(
    brain721CA,
    "getStakedAmount",
    [address]
  );

  return <span>{formatBalance(stakedAmount, isLoading, 18)}</span>;
}

export function BasedBalance() {
  const { data: basedBalance, isLoading } = useTokenBalance(basedCA);

  return <span>{formatBalance(basedBalance, isLoading)}</span>;
}

export function BrainCreditsBalance({ raw = false }) {
  const { data: brainCreditsBalance, isLoading } =
    useTokenBalance(brainCreditCA);

  return raw ? (
    brainCreditsBalance
  ) : (
    <span>{formatBalance(brainCreditsBalance, isLoading)}</span>
  );
}

export const useBrainCreditsBalance = () => {
  const { data: brainCreditsBalance } = useTokenBalance(brainCreditCA);

  return brainCreditsBalance?.formatted;
};

export const usePepecoinBalance = () => {
  const { data: pepecoinBalance } = useTokenBalance(pepecoinCA);
  return pepecoinBalance?.formatted.split(".")[0];
};

export const useBrainTokenBalances = () => {
  const [brainTokenBalances, setBrainTokenBalances] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const publicClient = usePublicClient();

  const { data: tokenCounter } = useReadContract({
    address: brain721CA,
    abi: BRAIN_NFT_ABI,
    functionName: "tokenCounter",
  });

  useEffect(() => {
    const fetchBalances = async () => {
      if (!tokenCounter) return;

      try {
        const totalTokens = Number(formatUnits(tokenCounter, 0));
        const activatedBrains = {};

        for (let i = 0; i < totalTokens; i++) {
          const erc20Address = await publicClient.readContract({
            address: brain721CA,
            abi: BRAIN_NFT_ABI,
            functionName: "brainToERC20",
            args: i,
          });
          activatedBrains[i] = erc20Address;
        }

        setBrainTokenBalances(activatedBrains);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching brain token balances:", err);
        setError(err.message || "Failed to fetch brain token balances");
        setLoading(false);
      }
    };

    fetchBalances();
  }, [tokenCounter, publicClient]);

  return { brainTokenBalances, loading, error };
};
